import styles from './index.module.scss';
import { next_tick, sleep } from '@vaaas/fpts/promise';
import { PropType, VNode, defineComponent, h, shallowRef, watch } from 'vue';
import { Option } from '@vaaas/fpts/option';
import { utility } from '@phi/components';

export type ComponentProvider = (route: Route) => VNode | string | Array<VNode | string>;

export type Route = (x: ComponentProvider) => void;

const empty_default: ComponentProvider = () => '';

export const VueRouter = defineComponent({
    props: {
        route: {
            required: true,
            type: Function as PropType<Option<ComponentProvider>>,
        },
    },

    emits: {
        'update:route': (x: ComponentProvider) => true,
    },

    setup(props, { emit }) {
        // state
        const current = shallowRef<ComponentProvider>(props.route || empty_default);
        const faded = shallowRef(false);
        const $el = shallowRef<HTMLElement|null>(null);

        // methods
        const on_route = (provider: ComponentProvider) => emit('update:route', provider);

        async function on_updated_route(value: Option<ComponentProvider>) {
            faded.value = true;
            if ($el.value)
                await sleep(125);
            else
                await next_tick();
            current.value = value || empty_default;
            await next_tick();
            faded.value = false;
        }

        // watchers
        watch(() => props.route, on_updated_route);

        // render
        return () => h('div', {
            ref: $el,
            class: {
                [styles.router]: true,
                [utility.important_opaque]: faded.value,
                [utility.important_scaled_down]: faded.value,
            },
        }, current.value(on_route));
    }
});
